// Global packages and components
import { useLocation, useNavigation } from '@remix-run/react'
import { BlogListerItem, Button, SchemaMeta, SchemaWrapper } from '~/components'

// Types
import type {
  BlogListingItem,
  SanityConceptItem,
  SanityRefDeconstructed,
} from '~/types'

interface Props {
  category?: SanityConceptItem
  /** Resolved on the server by resolveBlocks */
  posts?: BlogListingItem[]
  count?: number
  page?: number
}

// Main export
export default function BlockBlogListing({
  category,
  posts = [],
  count = 0,
  page = 1,
}: Props = {}) {
  const location = useLocation()
  const navigation = useNavigation()

  const pageRef = category?.pageReference
  const pageRefTitle =
    pageRef && typeof pageRef === 'object' && 'title' in pageRef
      ? (pageRef as SanityRefDeconstructed).title
      : undefined

  const listName = category?.prefLabel ?? pageRefTitle ?? 'Blog posts'

  // A real href, so a crawler can follow it to the older posts. Other params
  // are kept, as on the product listing pagination
  const params = new URLSearchParams(location.search)

  params.set('page', String(page + 1))

  const loadMoreHref = `${location.pathname}?${params.toString()}`

  // Load more stays on this page, so a pending navigation to it is ours
  const isLoadingMore =
    navigation.state === 'loading' &&
    navigation.location?.pathname === location.pathname

  return (
    <div className="container">
      {count > 0 ? (
        <>
          <SchemaWrapper
            type="ItemList"
            className="mb-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-10 xl:grid-cols-3"
          >
            <SchemaMeta
              prop="name"
              content={listName}
            />
            <SchemaMeta
              prop="numberOfItems"
              content={String(posts.length)}
            />
            {posts.map((e, i) => (
              <BlogListerItem
                key={i}
                {...e}
              />
            ))}
          </SchemaWrapper>
          <div className="text-center">
            {count > posts.length && (
              <div className="mb-4">
                <Button
                  title={isLoadingMore ? 'Loading…' : 'Load more'}
                  style="tealGreen"
                  to={loadMoreHref}
                  preventScrollReset
                  minWidth
                />
              </div>
            )}
            <p className="text-xs text-neutral-500">
              Showing&nbsp;
              <span>{posts.length}</span>
              &nbsp;of&nbsp;<span>{count}</span>&nbsp;entries
            </p>
          </div>
        </>
      ) : (
        <p className="pt-8 text-center text-2xl">No posts found</p>
      )}
    </div>
  )
}
